package com.partejar.app.core.ui.domain.gestante

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarStatusBadge
import com.partejar.app.core.ui.components.PartejarStatusBadgeTone
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme

data class GestanteSummary(
    val name: String,
    val babyName: String,
    val gestationalWeeks: Int?,
    val gestationalDays: Int?,
    val dueDate: String?,
    val responsible: String?,
    val status: GestanteSummaryStatus,
    val stage: GestanteStage? = null,
    val deliveryDate: String? = null
)

enum class GestanteStage {
    ACTIVE,
    WAITING_CLOSURE,
    PRESENTATION_MEETING,
    CAPTURE,
    POSTPARTUM
}

sealed interface GestanteSummaryStatus {
    data object OnTrack : GestanteSummaryStatus
    data class ActionRequired(val message: String) : GestanteSummaryStatus
}

@Composable
fun GestanteSummaryCard(
    summary: GestanteSummary,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    onActionClick: (() -> Unit)? = null
) {
    val content: @Composable () -> Unit = {
        when (summary.stage) {
            GestanteStage.POSTPARTUM -> PostpartumSummaryContent(summary, navigable = onClick != null)
            GestanteStage.ACTIVE -> ActiveSummaryContent(
                summary,
                navigable = onClick != null,
                onActionClick = onActionClick
            )
            else -> DefaultSummaryContent(summary, onActionClick)
        }
    }
    if (onClick == null) {
        PartejarCard(modifier = modifier.fillMaxWidth(), content = content)
    } else {
        PartejarCard(
            onClick = onClick,
            modifier = modifier.fillMaxWidth(),
            motionEnabled = summary.stage != null,
            content = content
        )
    }
}

@Composable
private fun DefaultSummaryContent(summary: GestanteSummary, onActionClick: (() -> Unit)?) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.Top
        ) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(summary.name, Modifier.semantics { heading() }, style = MaterialTheme.typography.titleMedium)
                summary.stage?.let { PartejarStatusBadge(text = it.label, tone = it.tone) }
                Text("Bebê: ${summary.babyName}", style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant)
                summary.responsible?.let {
                    Text("EO · $it", style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            GestationalAgeSummary(summary, prefix = null)
        }
        when (val status = summary.status) {
            GestanteSummaryStatus.OnTrack -> OnTrackStatus()
            is GestanteSummaryStatus.ActionRequired -> ActionRequiredArea(status.message, onActionClick)
        }
    }
}

@Composable
private fun ActiveSummaryContent(
    summary: GestanteSummary,
    navigable: Boolean,
    onActionClick: (() -> Unit)?
) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Row(horizontalArrangement = Arrangement.spacedBy(spacing.space8), verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        text = summary.name,
                        modifier = Modifier.weight(1f).semantics { heading() },
                        style = MaterialTheme.typography.titleMedium,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                    summary.stage?.let { stage ->
                        PartejarStatusBadge(text = stage.label, tone = stage.tone)
                    }
                }
                Text(
                    text = "Bebê: ${summary.babyName}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            GestationalAgeSummary(summary, prefix = null)
        }
        SummaryDivider()
        when (val status = summary.status) {
            GestanteSummaryStatus.OnTrack -> OnTrackStatus()
            is GestanteSummaryStatus.ActionRequired -> ActionRequiredArea(status.message, onActionClick)
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = summary.responsible?.let { "Responsável · $it" } ?: "Responsável não informado",
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            if (navigable) NavigationChevron()
        }
    }
}

@Composable
private fun PostpartumSummaryContent(summary: GestanteSummary, navigable: Boolean) {
    val spacing = PartejarTheme.spacing
    Column(verticalArrangement = Arrangement.spacedBy(spacing.space12)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space12),
            verticalAlignment = Alignment.Top
        ) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                Text(
                    text = summary.name,
                    modifier = Modifier.semantics { heading() },
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = "Bebê: ${summary.babyName}",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            PartejarStatusBadge(text = GestanteStage.POSTPARTUM.label, tone = PartejarStatusBadgeTone.NEUTRAL)
        }
        SummaryDivider()
        SummaryValue("Parto em", summary.deliveryDate ?: "Data não informada")
        GestationalAgeSummary(summary, prefix = "IG ao parto")
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = summary.responsible?.let { "Responsável · $it" } ?: "Responsável não informado",
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            if (navigable) NavigationChevron()
        }
    }
}

@Composable
private fun GestationalAgeSummary(summary: GestanteSummary, prefix: String?) {
    val weeks = summary.gestationalWeeks
    val days = summary.gestationalDays
    Column(
        horizontalAlignment = if (prefix == null) Alignment.End else Alignment.Start,
        verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)
    ) {
        if (weeks != null && days != null) {
            if (prefix == null) {
                GestationalAge(weeks, days)
                Text(
                    text = summary.dueDate?.let { "DPP $it" } ?: "DPP não informada",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            } else {
                SummaryValue(prefix, "${weeks}s ${days}d")
            }
        } else {
            Text(
                text = if (prefix == null) "IG não informada" else "$prefix · não informada",
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@Composable
private fun SummaryValue(label: String, value: String) {
    Row(horizontalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space4)) {
        Text("$label ·", style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant)
        Text(value, style = MaterialTheme.typography.labelLarge)
    }
}

@Composable
private fun NavigationChevron() {
    Icon(
        imageVector = PartejarIcons.Actions.next,
        contentDescription = "Abrir detalhes",
        modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
        tint = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

@Composable
private fun SummaryDivider() {
    HorizontalDivider(
        thickness = PartejarTheme.dimensions.strokeThin,
        color = MaterialTheme.colorScheme.outlineVariant
    )
}

private val GestanteStage.label: String
    get() = when (this) {
        GestanteStage.ACTIVE -> "Ativa"
        GestanteStage.WAITING_CLOSURE -> "Aguardando fechamento"
        GestanteStage.PRESENTATION_MEETING -> "Reunião"
        GestanteStage.CAPTURE -> "Captação"
        GestanteStage.POSTPARTUM -> "Encerrada"
    }

private val GestanteStage.tone: PartejarStatusBadgeTone
    get() = when (this) {
        GestanteStage.ACTIVE -> PartejarStatusBadgeTone.SUCCESS
        GestanteStage.WAITING_CLOSURE -> PartejarStatusBadgeTone.ATTENTION
        GestanteStage.PRESENTATION_MEETING -> PartejarStatusBadgeTone.INFO
        GestanteStage.CAPTURE -> PartejarStatusBadgeTone.NEUTRAL
        GestanteStage.POSTPARTUM -> PartejarStatusBadgeTone.NEUTRAL
    }

@Composable
private fun OnTrackStatus() {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    Row(
        horizontalArrangement = Arrangement.spacedBy(spacing.space4),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = PartejarIcons.Feedback.success,
            contentDescription = null,
            modifier = Modifier.size(dimensions.iconSmall),
            tint = PartejarTheme.colors.success
        )
        Text(
            text = "Acompanhamento em dia",
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
private fun ActionRequiredArea(
    message: String,
    onActionClick: (() -> Unit)?
) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.small,
        color = PartejarTheme.colors.attentionContainer,
        contentColor = PartejarTheme.colors.onAttentionContainer
    ) {
        Row(
            modifier = Modifier.padding(
                start = spacing.space12,
                end = spacing.space4
            ),
            horizontalArrangement = Arrangement.spacedBy(spacing.space8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Surface(
                modifier = Modifier.size(spacing.space8),
                shape = PartejarShapes.pill,
                color = PartejarTheme.colors.attention,
                content = {}
            )
            Text(
                text = message,
                modifier = Modifier.weight(1f),
                style = MaterialTheme.typography.labelMedium
            )
            if (onActionClick != null) {
                TextButton(
                    onClick = onActionClick,
                    modifier = Modifier.heightIn(min = dimensions.minimumTouchTarget)
                ) {
                    Text("Abrir →", style = MaterialTheme.typography.labelMedium)
                }
            } else {
                Spacer(modifier = Modifier.size(spacing.space4))
            }
        }
    }
}
